import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REG = {
  ack: "Acknowledgments", c21: "MOCR", a4: "Intake Determination", a8: "Screening", caps: "CAP Register",
  c22: "Verify Register", inc: "Incidents", trn: "Training", op: "Overpayments", baa: "BAA", kpi: "KPI",
  pack: "Committee Pack", fb: "Change Requests", vrep: "Alteva Reports", manual: "Manual",
};
const fmtAt = (at) => (at.length <= 10
  ? new Date(`${at}T12:00:00`).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" })
  : new Date(at).toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }));

export default function AuditTrail({ active }) {
  const [data, setData] = useState(null);
  const [person, setPerson] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-trail?limit=500`);
      setData(res.data);
    } catch {
      toast.error("Could not load the audit trail");
    }
  }, []);
  useEffect(() => { if (active === "trail") load(); }, [active, load]);

  const events = (data?.events || []).filter((e) => {
    if (!person) return true;
    if (person === "__none__") return !e.actor;
    return e.actor?.email === person;
  });

  return (
    <section className={`chapter utility${active === "trail" ? " active" : ""}`} id="trail">
      <div className="eyebrow">Working record · one timeline of every signed action across the system — who did what, when, where</div>
      <h1>Audit Trail</h1>
      <p className="lede">Every attestation, signature, register entry, closure, and adjustment — stamped with the account that made it — in a single chronological trail. Filter by person to answer "what did this account sign?" in one look.</p>

      <div className="month-bar">
        <label>Person</label>
        <select className="inp" value={person} onChange={(e) => setPerson(e.target.value)} data-testid="audit-person-filter">
          <option value="">Whole team ({data?.count ?? "…"} actions)</option>
          {(data?.actors || []).map((a) => (<option key={a.id} value={a.email}>{a.name} — {a.email}</option>))}
          <option value="__none__">Unattributed (pre-login records)</option>
        </select>
      </div>

      <div className="audit-list" data-testid="audit-timeline">
        {data === null ? (
          <p className="k-sub">Loading…</p>
        ) : events.length === 0 ? (
          <p className="k-sub" data-testid="audit-empty">No signed actions {person ? "for this account" : "yet"}.</p>
        ) : (
          events.map((e, i) => (
            <div className="audit-item" key={i} data-testid={`audit-event-${i}`}>
              <span className="audit-when">{fmtAt(e.at)}</span>
              <span className="audit-actor">{e.actor ? e.actor.name : "Unattributed"}</span>
              <span className="audit-action">{e.action}</span>
              <span className="audit-detail">{e.detail}</span>
              <span className="audit-reg">{REG[e.target] || e.target}</span>
            </div>
          ))
        )}
      </div>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Records created before per-person logins show as "Unattributed" — the name typed on the record still stands; only the account stamp is absent. The trail is read-only and assembled live from the underlying registers.</p>
    </section>
  );
}
